import { useEffect } from "react";
import {
  Container,
  Typography,
  TextField,
  Box,
  Divider,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "../../../../hooks/useTranslation";
import { Controller, FieldValues, FormProvider } from "react-hook-form";
import { useCustom, useCustomMutation } from "@refinedev/core";
import { useMarketplaceStore } from "../../../../store/useMarketplaceStore";
import { CurrencyRateSettings } from "./components/CurrencyRateSettings";
import { ActionButtons } from "../../../../components/form/ActionButtons";
import { useForm } from "../../../../hooks/useForm";
import { useQueryClient } from "@tanstack/react-query";
import { Info } from "@mui/icons-material";
import { TaxLiabilitySettings } from "./components/TaxLiabilitySettings";
import { SyncrosaleLoader } from "../../../../components/layout/components/SyncrosaleLoader";

export type GeneralSettingsFormValues = {
  storeName: string;
  maximumStockQuantity: string;
  criticalStockQuantity: string;
  amazonCommissionRate: string;
  currencyDisplay: CurrencyDisplay;
  currencyRateType: CurrencyRate;
  currencyRate: string | null;
  handlingTime: string;
  taxingSetting: TaxingSetting;
  taxThreshold: string | null;
  higherTaxPercentage: string | null;
  higherTaxFixedAmount: string | null;
  lowerTaxPercentage: string | null;
  lowerTaxFixedAmount: string | null;
  taxPercentage: string | null;
};

type CurrencyRate = "AUTOMATIC" | "MANUAL";
type CurrencyDisplay = "LOCAL_CURRENCY" | "USD";
type TaxingSetting = "CLOSED" | "ACTIVE";

export const GeneralSettings = () => {
  const selectedCountry = useMarketplaceStore((state) => state.selectedCountry);
  const { translate } = useTranslation("pages.stores.settings.generalSettings");
  const queryClient = useQueryClient();

  const methods = useForm<GeneralSettingsFormValues>();

  const {
    control,
    handleSubmit,
    reset,
    register,
    formState: { errors, isDirty },
  } = methods;

  const { data, isLoading, isFetching } = useCustom({
    url: `store/${selectedCountry?.id}/settings`,
    method: "get",
    queryOptions: {
      enabled: !!selectedCountry,
    },
  });

  // Set data from the API to the form
  useEffect(() => {
    const formValues = data?.data;
    if (!formValues) return;

    reset({
      ...formValues,
      storeName: selectedCountry?.storeName,
    });
  }, [data]);

  const { mutate, isLoading: isSaveLoading } = useCustomMutation();

  const onSubmit = async (data: FieldValues) => {
    const dataToSend = {
      ...data,
      maximumStockQuantity: Number(data.maximumStockQuantity),
      criticalStockQuantity: Number(data.criticalStockQuantity),
      amazonCommissionRate: Number(data.amazonCommissionRate),
      handlingTime: Number(data.handlingTime),
    } as unknown as GeneralSettingsFormValues;

    if (dataToSend.currencyRateType === "AUTOMATIC") {
      dataToSend.currencyRate = null;
    }

    if (dataToSend.taxingSetting === "CLOSED") {
      dataToSend.taxThreshold = null;
      dataToSend.higherTaxFixedAmount = null;
      dataToSend.higherTaxPercentage = null;
      dataToSend.lowerTaxFixedAmount = null;
      dataToSend.lowerTaxPercentage = null;
      dataToSend.taxPercentage = null;
    }

    mutate(
      {
        url: `store/${selectedCountry?.id}/settings`,
        method: "put",
        values: dataToSend,
        successNotification: () => {
          return {
            message: translate("updatedSuccessfully"),
            type: "success",
          };
        },
      },
      {
        onSuccess: (data) => {
          // Invalidate the marketplace query to refetch the data
          queryClient.invalidateQueries({
            queryKey: [
              "data",
              "default",
              "custom",
              {
                method: "get",
                url: "marketplace",
              },
            ],
          });

          reset(data?.data);
        },
      },
    );
  };

  const currencies = Array.from({ length: 20 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}`,
  }));

  if (isLoading || isFetching) {
    return <SyncrosaleLoader />;
  }

  return (
    <FormProvider {...methods}>
      <Container sx={{ padding: "16px 0" }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {[
            "storeName",
            "maximumStockQuantity",
            "criticalStockQuantity",
            "amazonCommissionRate",
          ].map((fieldName) => (
            <Box display="flex" alignItems="center" mb={2} key={fieldName}>
              <Typography
                variant="body2"
                sx={{ width: "20%", display: "flex", alignItems: "center" }}
                fontWeight="bold"
              >
                {translate(fieldName)}:
                {fieldName === "criticalStockQuantity" && (
                  <Tooltip
                    title={translate("criticalStockQuantityInfo")}
                    placement="right"
                    sx={{ marginX: 1 }}
                  >
                    <Info fontSize="small" color="primary" />
                  </Tooltip>
                )}
              </Typography>

              <TextField
                sx={{ width: "80%" }}
                size="small"
                error={!!errors[fieldName]}
                type={fieldName !== "storeName" ? "number" : "text"}
                helperText={
                  errors[fieldName]
                    ? translate("required", {
                        fieldName: translate(fieldName),
                      })
                    : ""
                }
                {...register(fieldName, { required: true })}
              />
            </Box>
          ))}

          <Divider sx={{ margin: "10px 0px" }} />
          <CurrencyRateSettings />
          <Divider sx={{ margin: "10px 0px" }} />
          <TaxLiabilitySettings />
          <Divider sx={{ margin: "10px 0px" }} />

          <Box display="flex" alignItems="center" mb={2}>
            <Typography variant="body2" fontWeight="bold" sx={{ width: "20%" }}>
              {translate("currencyDisplay")}:
            </Typography>
            <Box display="flex" flexDirection="column">
              <Controller
                control={control}
                name="currencyDisplay"
                render={({ field }) => (
                  <RadioGroup {...field} value={field.value ?? ""}>
                    <FormControlLabel
                      value="LOCAL_CURRENCY"
                      control={<Radio />}
                      label={translate("localCurrency")}
                    />
                    <FormControlLabel
                      value="USD"
                      control={<Radio />}
                      label={translate("USD")}
                    />
                  </RadioGroup>
                )}
              />
            </Box>
          </Box>

          <Divider sx={{ margin: "10px 0px" }} />

          <Box display="flex" alignItems="center" mb={2}>
            <Typography variant="body2" fontWeight="bold" sx={{ width: "20%" }}>
              {translate("handlingTime")}:
            </Typography>
            <Box
              display="flex"
              flexDirection="row"
              alignItems={"center"}
              gap={2}
            >
              <Controller
                name="handlingTime"
                control={control}
                render={({ field }) => (
                  <TextField
                    select
                    {...field}
                    value={field.value ?? ""}
                    size="small"
                  >
                    {currencies.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
              {translate("days")}
            </Box>
          </Box>

          <ActionButtons
            isLoading={isSaveLoading}
            isDirty={isDirty}
            reset={reset}
          />
        </form>
      </Container>
    </FormProvider>
  );
};
